import { useState, useEffect, useRef } from "react";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { OPENER } from "./content";
import { prefersReduced } from "./hooks";

export default function Pieces({ lang }) {
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);
  const n = OPENER.length;
  const go = (d) => setI((p) => (p + d + n) % n);
  const cur = OPENER[i];
  const touchX = useRef(null);

  // Autoplay driven by the progress-bar animation (per-slide duration).
  // Advance happens on the active dash's animationend; pausing the animation
  // (on hover) also pauses the countdown. Disabled for reduced motion.

  // Keyboard ← → (ignored while a lightbox is open).
  useEffect(() => {
    const onKey = (e) => {
      if (document.querySelector(".lightbox")) return;
      if (e.key === "ArrowLeft") go(-1);
      if (e.key === "ArrowRight") go(1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onTouchStart = (e) => {
    touchX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e) => {
    if (touchX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    if (Math.abs(dx) > 40) go(dx < 0 ? 1 : -1);
    touchX.current = null;
  };

  return (
    <section className="pieces-opener" id="pieces" data-testid="section-pieces">
      <div
        className="pieces-stage"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        {OPENER.map((s, idx) => (
          <div key={s.src} className={`pieces-slide ${idx === i ? "is-active" : ""}`} aria-hidden={idx !== i}>
            <img
              src={s.src}
              alt={lang === "fr" ? s.nameFr : s.nameEn}
              className="pieces-img"
              loading={idx === 0 ? "eager" : "lazy"}
            />
          </div>
        ))}

        <div className="pieces-veil" aria-hidden="true" />

        <div className="pieces-overlay">
          <div className="pieces-caption" key={i}>
            <span className="pieces-uni">{cur.uni}</span>
            <span className="pieces-name">{lang === "fr" ? cur.nameFr : cur.nameEn}</span>
            <p className="pieces-desc">{lang === "fr" ? cur.descFr : cur.descEn}</p>

            <div className="pieces-dots">
              {OPENER.map((s, idx) => (
                <button
                  key={idx}
                  className={`pieces-dash ${idx === i ? "is-active" : ""}`}
                  onClick={() => setI(idx)}
                  aria-label={`Slide ${idx + 1}`}
                  data-testid={`pieces-dash-${idx}`}
                >
                  {idx === i && !prefersReduced && (
                    <span
                      key={i}
                      className="pieces-dash__fill"
                      style={{
                        animationDuration: `${s.duration}ms`,
                        animationPlayState: paused ? "paused" : "running",
                      }}
                      onAnimationEnd={() => go(1)}
                    />
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="container pieces-arrows-wrap">
          <div className="pieces-arrows">
            <button className="pieces-arrow" onClick={() => go(-1)} aria-label="Précédent" data-testid="pieces-prev">
              <ArrowLeft size={20} strokeWidth={1.4} />
            </button>
            <button className="pieces-arrow" onClick={() => go(1)} aria-label="Suivant" data-testid="pieces-next">
              <ArrowRight size={20} strokeWidth={1.4} />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
